import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../hooks';
import { AuthShell, authInputStyle, authInputFocus, authInputBlur, authLabelStyle, authButtonStyle } from '../components/AuthShell';

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    if (isAuthenticated) setLoginError(null);
  }, [isAuthenticated]);

  if (isLoading && !loginError && !formData.email) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d0d0d' }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#FFD93D' }} />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setLoginError(null);
    setIsSubmitting(true);
    try {
      const result = await login({ email: formData.email, password: formData.password });
      if (!result.success) setLoginError(result.error || 'Invalid email or password');
    } catch (err: any) {
      setLoginError(err.message || 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Sign In - RRD Planners" description="Sign in to RRD Planners" />
      <AuthShell>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h2 style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>Welcome</h2>
          <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleSubmit}>
          {loginError && (
            <div style={{ background: 'rgba(229,115,115,0.1)', border: '1px solid #e57373', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#e57373', fontSize: 13 }}>
              {loginError}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={authLabelStyle}>Email</label>
            <input type="email" required value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
              placeholder="Enter your email" autoComplete="email" style={authInputStyle} onFocus={authInputFocus} onBlur={authInputBlur} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={authLabelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} required value={formData.password}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                placeholder="Enter your password" autoComplete="current-password"
                style={{ ...authInputStyle, paddingRight: 44 }} onFocus={authInputFocus} onBlur={authInputBlur} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4 }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginBottom: 20 }}>
            <a href="/auth/forgot-password" style={{ color: '#FFD93D', fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>Forgot password?</a>
          </div>

          <button type="submit" disabled={isSubmitting} style={authButtonStyle(isSubmitting)}>
            {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : 'Sign In'}
          </button>
        </form>

        <p style={{ color: '#666', fontSize: 14, marginTop: 24, textAlign: 'center' }}>
          Don't have an account?{' '}
          <a href="/auth/register" style={{ color: '#FFD93D', fontWeight: 600, textDecoration: 'none' }}>Sign up</a>
        </p>
      </AuthShell>
    </>
  );
};

export default LoginPage;
